import * as React from 'react';
import { type Subscription, useSubscription } from 'use-subscription';

import type { HiveWorkingBranch, HiveWorkingBranchState } from '@gitbook/hive-client';
import {
    applyRevisionComputations,
    type Revision,
    type RevisionComputations,
} from '@gitbook/hive-core';
import { useStateWithDeps } from '@gitbook/util-react-hooks';
import { assertNever } from '@gitbook/util-ts';

import { useHiveClient } from './useHiveClient';

const HiveRevisionContext = React.createContext<HiveWorkingBranchState | null>(null);

interface UseHiveWorkingBranchOptions {
    /**
     * If true, the hook will subscribe to future updates in the branch.
     * @default true
     */
    subscribe?: boolean;
}

/**
 * Provide a revision to the react tree.
 * The revision can come from a branch or be a frozen one.
 */
export function HiveRevisionProvider(
    props: React.PropsWithChildren<
        | {
              revision: Revision;
              branch?: undefined;
          }
        | {
              revision?: undefined;
              branch: HiveWorkingBranch;
          }
    >
): React.ReactElement {
    const { revision, branch, children } = props;

    if (revision) {
        return <ProviderFrozenRevision revision={revision}>{children}</ProviderFrozenRevision>;
    }

    if (branch) {
        return <ProviderWorkingBranch branch={branch}>{children}</ProviderWorkingBranch>;
    }

    assertNever(props);
}

function ProviderFrozenRevision(
    props: React.PropsWithChildren<{ revision: Revision }>
): React.ReactElement {
    const { revision: sourceRevision, children } = props;

    const client = useHiveClient();

    const contentComputer = client.getContentComputer(sourceRevision.contentId);

    const [computationsState, setComputationsState] = useStateWithDeps<RevisionComputations | null>(
        null,
        [sourceRevision]
    );

    const stream = React.useMemo(() => {
        return contentComputer.computeRevision({
            revision: sourceRevision,
            onUpdate: setComputationsState,
        });
    }, [contentComputer, sourceRevision, setComputationsState]);

    // Cancel the stream if it changes
    React.useEffect(() => {
        return () => {
            stream.cancel();
        };
    }, [stream]);

    const computations = computationsState ?? stream.result;
    const state = React.useMemo(() => {
        const applyResult = applyRevisionComputations({
            revision: sourceRevision,
            computations: computations,
        });

        return {
            revision: applyResult.result,
            sourceRevision,
            isComputed: applyResult.complete,
            computations: computations,
            cleanupListeners: () => {},
        };
    }, [computations, sourceRevision]);

    return <HiveRevisionContext.Provider value={state}>{children}</HiveRevisionContext.Provider>;
}

function ProviderWorkingBranch(
    props: React.PropsWithChildren<{ branch: HiveWorkingBranch }>
): React.ReactElement {
    const { branch, children } = props;

    const state = useHiveWorkingBranch(branch);
    return <HiveRevisionContext.Provider value={state}>{children}</HiveRevisionContext.Provider>;
}

/**
 * Get the current revision from the context.
 */
export function useHiveRevision(): HiveWorkingBranchState {
    const current = React.useContext(HiveRevisionContext);
    if (!current) {
        throw new Error('No revision provided');
    }
    return current;
}

/**
 * Get the current revision of a branch.
 * This hook suspends rendering while fetching the latest revision.
 */
export function useHiveWorkingBranch(
    branch: HiveWorkingBranch,
    options?: UseHiveWorkingBranchOptions
): HiveWorkingBranchState;
export function useHiveWorkingBranch(branch: null, options?: UseHiveWorkingBranchOptions): null;
export function useHiveWorkingBranch(
    branch: HiveWorkingBranch | null,
    options?: UseHiveWorkingBranchOptions
): HiveWorkingBranchState | null;
export function useHiveWorkingBranch(
    branch: HiveWorkingBranch | null,
    options: UseHiveWorkingBranchOptions = {}
): HiveWorkingBranchState | null {
    const { subscribe = true } = options;

    const subscription = React.useMemo<Subscription<HiveWorkingBranchState | null>>(() => {
        return {
            getCurrentValue: () => {
                if (!branch) {
                    return null;
                }

                return branch.state;
            },
            subscribe: (callback) => {
                if (!subscribe) {
                    return () => {};
                }

                if (!branch) {
                    return () => {};
                }

                return branch.listen('update', callback);
            },
        };
    }, [branch, subscribe]);

    return useSubscription(subscription);
}
